import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import ModalPortal from '../components/ModalPortal';
import {
  CreditCard,
  FileText,
  Loader2,
  ExternalLink,
  Check,
  Settings2,
  Plus,
  Pencil,
  X
} from 'lucide-react';

const BillingInvoice = () => {
  const { currentWorkspace, subscriptionInfo, refreshWorkspaces } = useWorkspace();
  const [activeTab, setActiveTab] = useState('billing');
  const [plans, setPlans] = useState([]);
  const [subscriptionCurrent, setSubscriptionCurrent] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);
  const [managePlans, setManagePlans] = useState([]);
  const [managePlansLoading, setManagePlansLoading] = useState(false);
  const [planModal, setPlanModal] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);

  const isWorkspace1Admin = Number(currentWorkspace?.id) === 1 && subscriptionInfo?.is_super_admin;

  const loadBilling = useCallback(async () => {
    setPlansLoading(true);
    try {
      const [plansRes, subRes] = await Promise.all([
        apiClient.get('/api/plans'),
        apiClient.get('/api/subscription/current')
      ]);
      if (plansRes.data?.success) setPlans(plansRes.data.plans || []);
      if (subRes.data?.success) setSubscriptionCurrent(subRes.data);
    } catch (e) {
      toast.error('Failed to load billing');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await apiClient.get('/api/subscription/invoices');
      if (res.data?.success) setInvoices(res.data.invoices || []);
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  const loadManagePlans = useCallback(async () => {
    setManagePlansLoading(true);
    try {
      const res = await apiClient.get('/api/plans/manage');
      if (res.data?.success) setManagePlans(res.data.plans || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load plans');
    } finally {
      setManagePlansLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'billing') loadBilling();
    if (activeTab === 'invoices') loadInvoices();
    if (activeTab === 'plans' && isWorkspace1Admin) loadManagePlans();
  }, [activeTab, loadBilling, loadInvoices, loadManagePlans, isWorkspace1Admin]);

  useEffect(() => {
    if (!window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const trialDaysLeft = (trialEnd) => {
    if (!trialEnd) return null;
    const end = new Date(trialEnd);
    const now = new Date();
    const diff = Math.ceil((end - now) / (24 * 60 * 60 * 1000));
    return diff > 0 ? diff : 0;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const x = typeof d === 'number' ? new Date(d * 1000) : new Date(d);
    return x.toLocaleDateString(undefined, { dateStyle: 'medium' });
  };

  const handleUpgrade = async (planId, billingCycle = 'monthly') => {
    try {
      setUpgradingPlanId(planId);
      const { data } = await apiClient.post('/api/subscription/create-order', {
        plan_id: planId,
        billing_cycle: billingCycle
      });
      if (!data.order_id || !data.key) {
        toast.error(data.error || 'Payment not configured');
        return;
      }
      const options = {
        key: data.key,
        order_id: data.order_id,
        name: 'Leads Automater',
        handler: async (res) => {
          await apiClient.post('/api/subscription/verify-payment', {
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_signature: res.razorpay_signature
          });
          toast.success('Payment successful');
          await loadBilling();
          if (typeof refreshWorkspaces === 'function') {
            await refreshWorkspaces();
          }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to upgrade');
    } finally {
      setUpgradingPlanId(null);
    }
  };

  const handleSavePlan = async (payload) => {
    setPlanSaving(true);
    try {
      if (planModal?.id) {
        await apiClient.put(`/api/plans/${planModal.id}`, payload);
        toast.success('Plan updated');
      } else {
        await apiClient.post('/api/plans', payload);
        toast.success('Plan created');
      }
      setPlanModal(null);
      loadManagePlans();
      loadBilling();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save plan');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleTogglePlanActive = async (plan) => {
    try {
      await apiClient.patch(`/api/plans/${plan.id}`, { is_active: !plan.is_active });
      toast.success(plan.is_active ? 'Plan deactivated' : 'Plan activated');
      loadManagePlans();
      loadBilling();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update plan');
    }
  };

  const isLifetimeSubscription =
    subscriptionInfo?.workspace_comped_lifetime === true ||
    subscriptionCurrent?.is_lifetime_plan === true ||
    subscriptionInfo?.is_lifetime_plan === true ||
    subscriptionCurrent?.plan?.slug === 'internal-lifetime';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing & Invoice</h1>
        <p className="text-gray-600">Manage your subscription and view invoices.</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('billing')}
              className={`py-3 px-6 font-medium text-sm flex items-center gap-2 ${activeTab === 'billing'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <CreditCard className="h-4 w-4" />
              Billing
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-3 px-6 font-medium text-sm flex items-center gap-2 ${activeTab === 'invoices'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <FileText className="h-4 w-4" />
              Invoices
            </button>
            {isWorkspace1Admin && (
              <button
                onClick={() => setActiveTab('plans')}
                className={`py-3 px-6 font-medium text-sm flex items-center gap-2 ${activeTab === 'plans'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <Settings2 className="h-4 w-4" />
                Plans
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'billing' && (
            <>
              {plansLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {subscriptionCurrent?.plan && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-700">Current plan: {subscriptionCurrent.plan.name}</p>
                      <p className="text-sm text-gray-500">
                        Workspaces: {subscriptionCurrent.workspace_count ?? 0} / {subscriptionCurrent.max_workspaces ?? 1}
                      </p>
                      {isLifetimeSubscription && (
                        <p className="text-sm text-blue-700 mt-1">Access does not expire — no renewal date.</p>
                      )}
                      {subscriptionCurrent.subscription?.status === 'trial' && (
                        <p className="text-sm text-blue-700 mt-1">
                          Trial active – {trialDaysLeft(subscriptionCurrent.subscription.trial_end)} day(s) left
                        </p>
                      )}
                      {!isLifetimeSubscription && subscriptionCurrent.subscription?.status === 'trial' && subscriptionCurrent.subscription?.trial_end && (
                        <p className="text-sm text-gray-500">Next billing: {formatDate(subscriptionCurrent.subscription.trial_end)}</p>
                      )}
                      {!isLifetimeSubscription && subscriptionCurrent.subscription?.status === 'active' && subscriptionCurrent.subscription?.current_period_end && (
                        <p className="text-sm text-gray-500">Next billing: {formatDate(subscriptionCurrent.subscription.current_period_end)}</p>
                      )}
                      {(subscriptionCurrent.subscription_expired || subscriptionCurrent.subscription?.status === 'expired') && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-800 font-medium">Your subscription has expired.</p>
                          <Link to="/billing" className="text-sm text-amber-700 font-medium underline mt-1 inline-block">
                            Renew in Billing
                          </Link>
                        </div>
                      )}
                      {subscriptionCurrent.subscription?.status === 'failed' && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-800 font-medium">Payment failed. Update your payment method.</p>
                          <Link to="/billing" className="text-sm text-red-700 font-medium underline mt-1 inline-block">
                            Update in Billing
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {!isLifetimeSubscription && (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose your plan</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {plans.filter((p) => (p.price_paise_monthly || p.price_paise) > 0 || (p.price_paise_yearly || 0) > 0).map((plan) => {
                          const currentPlanId = subscriptionCurrent?.plan?.id;
                          const currentPlanSlug = String(subscriptionCurrent?.plan?.slug || '').toLowerCase();
                          const planSlug = String(plan.slug || '').toLowerCase();
                          const isCurrent =
                            (currentPlanId != null && Number(currentPlanId) === Number(plan.id)) ||
                            (currentPlanSlug && currentPlanSlug === planSlug);
                          const subStatus = String(subscriptionCurrent?.subscription?.status || '').toLowerCase();
                          const needsRenew =
                            subscriptionCurrent?.subscription_expired ||
                            subStatus === 'expired' ||
                            subStatus === 'failed' ||
                            subStatus === 'cancelled' ||
                            subStatus === 'past_due';
                          const isTrialPlan = isCurrent && subStatus === 'trial';
                          const showAsActiveCurrent = isCurrent && !needsRenew;
                          const pm = plan.price_paise_monthly != null ? plan.price_paise_monthly : plan.price_paise;
                          const py = plan.price_paise_yearly != null ? plan.price_paise_yearly : 0;
                          return (
                            <div
                              key={plan.id}
                              className={`group relative rounded-2xl p-5 border shadow-sm transition-all ${showAsActiveCurrent
                                  ? 'border-blue-500 bg-blue-50/40 shadow-md'
                                  : isCurrent && needsRenew
                                    ? 'border-amber-400 bg-amber-50/40 shadow-md'
                                    : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                                }`}
                            >
                              {showAsActiveCurrent && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white">
                                    {isTrialPlan ? 'Trial Plan' : 'Active Plan'}
                                  </span>
                                </div>
                              )}
                              {isCurrent && needsRenew && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-600 text-white">
                                    Renew plan
                                  </span>
                                </div>
                              )}
                              <h4 className="font-semibold text-gray-900 text-lg">{plan.name}</h4>
                              {plan.description && (
                                <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                              )}
                              <div className="mt-3 space-y-1 text-sm">
                                {(pm || 0) > 0 && (
                                  <p className="text-2xl font-bold text-gray-900">
                                    ₹{(Number(pm) / 100).toFixed(0)}
                                    <span className="text-sm font-normal text-gray-500">/month</span>
                                  </p>
                                )}
                                {(py || 0) > 0 && (
                                  <p className="text-lg font-semibold text-gray-800">
                                    ₹{(Number(py) / 100).toFixed(0)}
                                    <span className="text-sm font-normal text-gray-500">/year</span>
                                  </p>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">{plan.max_workspaces} workspace(s)</p>
                              {plan.max_contacts_per_workspace != null && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {plan.max_contacts_per_workspace} contacts/workspace
                                </p>
                              )}
                              {!showAsActiveCurrent && (
                                <div className="mt-3 flex flex-col gap-2">
                                  {(pm || 0) > 0 && (
                                    <button
                                      onClick={() => handleUpgrade(plan.id, 'monthly')}
                                      disabled={upgradingPlanId === plan.id}
                                      className="w-full py-2 px-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                    >
                                      {isCurrent && needsRenew ? 'Renew — Monthly' : 'Pay — Monthly'}
                                    </button>
                                  )}
                                  {(py || 0) > 0 && (
                                    <button
                                      onClick={() => handleUpgrade(plan.id, 'yearly')}
                                      disabled={upgradingPlanId === plan.id}
                                      className="w-full py-2 px-3 border border-indigo-200 text-indigo-800 text-sm font-medium rounded-lg hover:bg-indigo-50 disabled:opacity-50"
                                    >
                                      {isCurrent && needsRenew ? 'Renew — Yearly' : 'Pay — Yearly'}
                                    </button>
                                  )}
                                </div>
                              )}
                              {showAsActiveCurrent && (
                                <div className="mt-3 flex items-center text-blue-600 text-sm font-medium">
                                  <Check className="w-4 h-4 mr-1" />
                                  {isTrialPlan ? 'You are currently on trial for this plan' : 'You are currently on this plan'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="mt-4 text-sm text-gray-500">
                        Manage subscription and cancel options on this Billing page.
                      </p>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'plans' && isWorkspace1Admin && (
            <>
              {managePlansLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Plans</h3>
                    <button
                      onClick={() => setPlanModal({})}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4" />
                      Add Plan
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mo / Yr (₹)</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workspaces</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacts</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {managePlans.map((plan) => {
                          const slug = String(plan.slug || '').toLowerCase();
                          const isProtectedPlan = slug === 'free' || slug === 'internal-lifetime';
                          return (
                          <tr key={plan.id} className={`hover:bg-gray-50 ${!plan.is_active ? 'opacity-60' : ''}`}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              <div>{plan.name}</div>
                              {plan.description && (
                                <div className="text-xs font-normal text-gray-500 mt-0.5">{plan.description}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 font-mono">{plan.slug}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div>
                                {(plan.price_paise_monthly != null ? plan.price_paise_monthly : plan.price_paise) > 0
                                  ? `₹${(Number(plan.price_paise_monthly != null ? plan.price_paise_monthly : plan.price_paise) / 100).toFixed(0)}/mo`
                                  : '—'}
                              </div>
                              <div className="text-gray-600">
                                {plan.price_paise_yearly > 0
                                  ? `₹${(Number(plan.price_paise_yearly) / 100).toFixed(0)}/yr`
                                  : ''}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{plan.max_workspaces}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <span>{plan.max_contacts_per_workspace != null ? `${plan.max_contacts_per_workspace}/workspace` : '—'}</span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Account total:{' '}
                                {plan.total_contacts_limit != null && plan.total_contacts_limit !== ''
                                  ? plan.total_contacts_limit
                                  : '∞'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${plan.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {plan.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => setPlanModal(plan)}
                                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                {isProtectedPlan ? (
                                  <span className="inline-flex items-center px-2 py-1 text-xs text-gray-500 rounded-lg bg-gray-100">
                                    Protected
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => handleTogglePlanActive(plan)}
                                    className={`p-2 rounded-lg ${plan.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                                    title={plan.is_active ? 'Deactivate' : 'Activate'}
                                  >
                                    {plan.is_active ? 'Deactivate' : 'Activate'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    Changing plan price creates a new Razorpay plan. Existing subscriptions keep the old plan until they renew.
                  </p>
                </>
              )}
            </>
          )}

          {activeTab === 'invoices' && (
            <>
              {invoicesLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No invoices yet</p>
                  <p className="text-sm text-gray-400 mt-1">Invoices will appear here after you subscribe to a paid plan.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issued</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900 font-mono">{inv.id}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                                inv.status === 'issued' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                              }`}>
                              {inv.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            ₹{((inv.amount || 0) / 100).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(inv.issued_at)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{inv.paid_at ? formatDate(inv.paid_at) : '—'}</td>
                          <td className="px-4 py-3">
                            {inv.short_url && (
                              <a
                                href={inv.short_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline inline-flex items-center gap-1 text-sm"
                              >
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {planModal !== null && (
        <PlanFormModal
          plan={planModal}
          onClose={() => setPlanModal(null)}
          onSave={handleSavePlan}
          saving={planSaving}
        />
      )}
    </div>
  );
};

function PlanFormModal({ plan, onClose, onSave, saving }) {
  const pmInit = plan.price_paise_monthly != null ? plan.price_paise_monthly : plan.price_paise;
  const pyInit = plan.price_paise_yearly != null ? plan.price_paise_yearly : 0;
  const [name, setName] = useState(plan.name || '');
  const [description, setDescription] = useState(plan.description || '');
  const [slug, setSlug] = useState(plan.slug || '');
  const [maxWorkspaces, setMaxWorkspaces] = useState(plan.max_workspaces ?? 1);
  const [priceMonthlyInr, setPriceMonthlyInr] = useState(pmInit != null ? String(pmInit / 100) : '');
  const [priceYearlyInr, setPriceYearlyInr] = useState(pyInit ? String(pyInit / 100) : '');
  const [maxContactsPerWs, setMaxContactsPerWs] = useState(
    plan.max_contacts_per_workspace != null ? String(plan.max_contacts_per_workspace) : '5000'
  );
  const [totalContactsLimit, setTotalContactsLimit] = useState(
    plan.total_contacts_limit != null && plan.total_contacts_limit !== ''
      ? String(plan.total_contacts_limit)
      : ''
  );
  const [blockExportOnTrial, setBlockExportOnTrial] = useState(
    plan.disable_contact_export_on_trial === false || plan.disable_contact_export_on_trial === 0 ? false : true
  );
  const isEdit = !!plan.id;

  useEffect(() => {
    const pm = plan.price_paise_monthly != null ? plan.price_paise_monthly : plan.price_paise;
    const py = plan.price_paise_yearly != null ? plan.price_paise_yearly : 0;
    setName(plan.name || '');
    setDescription(plan.description || '');
    setSlug(plan.slug || '');
    setMaxWorkspaces(plan.max_workspaces ?? 1);
    setPriceMonthlyInr(pm != null ? String(pm / 100) : '');
    setPriceYearlyInr(py ? String(py / 100) : '');
    setMaxContactsPerWs(plan.max_contacts_per_workspace != null ? String(plan.max_contacts_per_workspace) : '5000');
    setTotalContactsLimit(
      plan.total_contacts_limit != null && plan.total_contacts_limit !== '' ? String(plan.total_contacts_limit) : ''
    );
    setBlockExportOnTrial(
      plan.disable_contact_export_on_trial === false || plan.disable_contact_export_on_trial === 0 ? false : true
    );
  }, [
    plan.id,
    plan.name,
    plan.description,
    plan.slug,
    plan.max_workspaces,
    plan.price_paise,
    plan.price_paise_monthly,
    plan.price_paise_yearly,
    plan.max_contacts_per_workspace,
    plan.total_contacts_limit,
    plan.disable_contact_export_on_trial
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const pmPaise = Math.round(parseFloat(priceMonthlyInr || 0) * 100);
    const pyPaise = Math.round(parseFloat(priceYearlyInr || 0) * 100);
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const slugVal = slug.trim() || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!slugVal) {
      toast.error('Slug is required');
      return;
    }
    const maxPerWs = Math.max(1, parseInt(maxContactsPerWs, 10) || 1);
    let totalCap = null;
    if (String(totalContactsLimit).trim() !== '') {
      totalCap = Math.max(0, parseInt(totalContactsLimit, 10));
      if (Number.isNaN(totalCap)) {
        toast.error('Total contacts limit must be a number or empty for unlimited');
        return;
      }
    }
    onSave({
      name: name.trim(),
      description: description.trim(),
      slug: slugVal,
      max_workspaces: Math.max(1, maxWorkspaces),
      price_paise_monthly: pmPaise,
      price_paise_yearly: pyPaise,
      price_paise: pmPaise,
      billing_interval: pmPaise > 0 || pyPaise > 0 ? 'monthly' : null,
      max_contacts_per_workspace: maxPerWs,
      total_contacts_limit: totalCap,
      disable_contact_export_on_trial: blockExportOnTrial
    });
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 overflow-y-auto py-8">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 my-auto max-h-[92vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Plan' : 'Create Plan'}</h3>
            <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Pro"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="e.g. pro"
              />
              <p className="text-xs text-gray-500 mt-1">Lowercase, hyphens only.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (public)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="What this plan provides"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Workspaces</label>
              <input
                type="number"
                min="1"
                value={maxWorkspaces}
                onChange={(e) => setMaxWorkspaces(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly price (INR)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceMonthlyInr}
                  onChange={(e) => setPriceMonthlyInr(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yearly price (INR)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceYearlyInr}
                  onChange={(e) => setPriceYearlyInr(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">Set both to offer monthly and yearly billing; Razorpay gets two plan IDs.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max contacts per workspace</label>
              <input
                type="number"
                min="1"
                value={maxContactsPerWs}
                onChange={(e) => setMaxContactsPerWs(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total contacts limit (account-wide add + import)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={totalContactsLimit}
                onChange={(e) => setTotalContactsLimit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="empty = unlimited"
              />
              <p className="text-xs text-gray-500 mt-1">Across all workspaces you own. Leave empty for no total cap.</p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={blockExportOnTrial}
                onChange={(e) => setBlockExportOnTrial(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">
                Block exporting contacts (CSV) while the account is on <strong>free trial</strong>
              </span>
            </label>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

export default BillingInvoice;
